import React, { useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { BellIcon, ReceiptIcon, SettingsIcon, UserIcon, UsersIcon } from 'lucide-react'

import { Button, Card } from '@repo/ui'

/**
 * Nav + menu chrome — the two decisions today's theme answers inconsistently, with the numbers
 * printed beside each option so the choice is not made off a screenshot.
 *
 * ## Every variant renders in BOTH modes, always
 *
 * Not "in whichever mode you are currently in". The first version of this lab did that, and it
 * was close to useless: the popover problem is a LIGHT-mode problem, so all four Panel B
 * variants share one set of dark surfaces and differ in dark only by shadow weight. Viewed in
 * dark they read as identical — correctly, since dark needs no change, but that is not
 * something a viewer can tell from three boxes that look the same.
 *
 * Worse, the argument here IS the comparison between modes: the same token inverts direction.
 * A lab that shows one mode at a time asks the viewer to hold the other one in memory, which
 * is exactly the thing the eye is bad at. So each cell paints its own surfaces from explicit
 * hex values and ignores the ambient theme.
 *
 * ## Panel A — what marks a hovered or selected row
 *
 * The app gives two answers today and neither was decided:
 *
 *   main Sidebar row      neutral overlay, chroma 0 — and already mode-aware
 *   SideNav pill          brand @ 0.12 / 0.16, identical in both modes
 *   dropdown MenuItem     brand @ 0.12 / 0.16, identical in both modes
 *
 * The pill's purple is outright accidental: `SideNav`'s own `sx` sets the selected row to
 * `primary[50]` (grey #f6f6f6), and the theme's global `MuiMenuItem` override lands later in
 * the cascade at equal specificity and wins. Whoever wrote SideNav believed it was grey.
 *
 * The asymmetry that makes light feel wrong with the SAME token, measured on the same surface
 * (the sidebar) in both modes so it is a fair comparison:
 *
 *   dark,  brand @0.16 over #18181b -> ΔL +0.009   (row gets LIGHTER)
 *   light, brand @0.16 over #fafafa -> ΔL -0.201   (row gets DARKER, ~20x the magnitude)
 *
 * In dark a brand tint lifts the surface, so selection reads as illuminated. In light the
 * identical rule pushes it down 0.20 luminance, so it reads as a stain — and its chroma lands
 * at high luminance, where hue is most visible, on a page whose chroma is 1. It becomes the
 * only coloured object on screen.
 *
 * Two further measurements that "grey vs purple" hides. The relative STRENGTH of the two
 * treatments flips between modes: in light the sidebar is the fainter (1.092 vs the menu's
 * 1.180), in dark it is the stronger (1.246 vs 1.102). And in dark a neutral white overlay
 * separates BETTER than brand at any alpha tested (1.329 vs 1.168) — so dark's purple is doing
 * its work through chroma, not luminance, which is precisely why copying its alpha into light
 * fails.
 *
 * The precedent for the fix is already here: the sidebar's neutral overlay is mode-aware today
 * (black 4% light, white 8% dark — MUI's `action.hover`). Per-mode base and amount is not a new
 * principle; the brand tint is just the one place it was never applied.
 *
 * ## Panel B — what defines a popover against what it sits on
 *
 * Ordering the real tokens by luminance shows dark and light are not the same shape:
 *
 *   dark    page #09090b  ->  popover #0f0f12  ->  sidebar #18181b   (steps 1.04 / 1.08)
 *   light   page #f4f5f5  ->  sidebar #fafafa  ->  popover #ffffff   (steps 1.046 / 1.044)
 *
 * Note what that does NOT say: dark's fill steps are not bigger, they are the same size. Dark
 * reads better because its popover sits BETWEEN page and sidebar instead of being pinned at a
 * ceiling, and because its edge reads.
 *
 * The light border is the actual defect. `divider` is rgba(0,0,0,0.12) = #e0e0e0 on white
 * paper: 1.315:1 against its own fill, but only 1.204:1 against the PAGE it overlaps — and the
 * page is what an edge is seen against. A line needs about 1.3:1, so the edge does not read,
 * which is why it needed a heavy shadow to look attached to anything.
 *
 *   0.12 (today)  #e0e0e0   1.204:1 vs page   under threshold
 *   0.16          #d6d6d6   1.328:1           clears it
 *   0.20          #cccccc   1.470:1           comfortable
 *
 * A true three-surface ramp is reachable in light, but only by lowering the PAGE, since the
 * popover cannot go above white:
 *
 *   today          #f4f5f5 / #fafafa / #ffffff   steps 1.046 / 1.044
 *   lowered page   #e9ebeb / #f4f5f5 / #ffffff   steps 1.096 / 1.092   <- beats dark's own
 *
 * ## Why mock compositions rather than applying each variant for real
 *
 * Panel B's variants change the PAGE background, which is global — applying one would restyle
 * the lab itself, so no two could be seen at once and the comparison would again be a sequence
 * of screenshots. Each cell paints a miniature page with a sidebar strip and an overlapping
 * popover at the real token values. The composition is what is being judged, so the
 * composition is what is shown.
 */

// ---------------------------------------------------------------------------
// Local colour maths. Kept here rather than imported because printing the numbers beside each
// swatch is the whole point: the eye is least reliable exactly where these options differ.
// ---------------------------------------------------------------------------

type RGB = [number, number, number]
type Mode = 'light' | 'dark'

const hexToRgb = (h: string): RGB => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)) as RGB

const channel = (v: number) => {
  const s = v / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

const luminance = (c: RGB) =>
  0.2126 * channel(c[0]) + 0.7152 * channel(c[1]) + 0.0722 * channel(c[2])

const contrast = (a: RGB, b: RGB) => {
  const [l1, l2] = [luminance(a), luminance(b)]
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

/** Composite a translucent colour over an opaque backdrop — the step most easily got wrong. */
const composite = (fg: RGB, bg: RGB, a: number): RGB =>
  fg.map((v, i) => v * a + bg[i] * (1 - a)) as RGB

const toHex = (c: RGB) => `#${c.map(v => Math.round(v).toString(16).padStart(2, '0')).join('')}`

const chroma = (c: RGB) => Math.round(Math.max(...c) - Math.min(...c))

/**
 * CIELAB ΔE — perceptual distance, where contrast ratio is luminance only.
 *
 * Needed because the editorial options separate surfaces by TEMPERATURE, and a warm/cool shift
 * is close to a no-op on the luminance axis while being clearly visible. Measured: a cool page
 * against an ivory panel is 1.084:1 (which sounds invisible) but ΔE 4.23, versus today's white
 * panel at 1.092:1 and ΔE 3.55. About 2.3 is the just-noticeable threshold for large areas.
 *
 * This matters structurally, not just aesthetically: white is a ceiling for luminance in light
 * mode, which is what forced elevation earlier. Temperature has no ceiling.
 */
const toLab = (c: RGB) => {
  const [r, g, b] = c.map(v => {
    const s = v / 255
    return s > 0.04045 ? Math.pow((s + 0.055) / 1.055, 2.4) : s / 12.92
  })
  const X = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047
  const Y = r * 0.2126 + g * 0.7152 + b * 0.0722
  const Z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116)
  return [116 * f(Y) - 16, 500 * (f(X) - f(Y)), 200 * (f(Y) - f(Z))]
}

const deltaE = (a: RGB, b: RGB) => {
  const [A, B] = [toLab(a), toLab(b)]
  return Number(Math.sqrt(A.reduce((s, v, i) => s + (v - B[i]) ** 2, 0)).toFixed(2))
}

const BRAND = '#7F48DF'
const BRAND_RGB = hexToRgb(BRAND)

/**
 * Text and neutral-overlay values per mode, so a cell can paint a light composition while the
 * app is in dark. A neutral overlay MUST flip base with the mode — black over near-black
 * measures ΔL -0.001 at 1.009:1, i.e. invisible. (This was black in both modes at first, which
 * quietly rigged the comparison: the neutral option looked unusable in dark for a reason that
 * was the lab's fault rather than the option's.)
 */
const MODE_TOKENS: Record<Mode, { text: string; dim: string; neutral: RGB }> = {
  light: { text: '#18181b', dim: '#71717a', neutral: [0, 0, 0] },
  dark: { text: '#fafafa', dim: '#a1a1aa', neutral: [255, 255, 255] },
}

const ModeTag = ({ mode }: { mode: Mode }) => (
  <Typography variant="body4" sx={{ fontWeight: 600, letterSpacing: '0.08em', opacity: 0.5 }}>
    {mode.toUpperCase()}
  </Typography>
)

// ---------------------------------------------------------------------------
// Panel A
// ---------------------------------------------------------------------------

/**
 * A row treatment, decomposed into the four things that actually carry the "register" of a UI:
 * what fills the row, what marks it, how round it is, and whether rows sit on rules.
 *
 * Splitting it this way is the point. "Mode-aware brand tint + 2px marker" was rejected as
 * dev-esque, and that critique is specific rather than vague: a rounded tinted pill with a 2px
 * left accent bar is the house style of Linear, Vercel, GitHub and Notion, so it reads as
 * developer tooling because those products ARE the reference. The radius is part of it — a
 * radius exists to make a fill look friendly, which is a consumer-SaaS instinct.
 *
 * The Christie's / Sotheby's / Artsy register inverts every one of those choices: hairlines
 * rather than fills, whitespace rather than tint, type weight rather than colour, square rather
 * than rounded, warm neutrals rather than cool greys. Depth comes from ruled lines and paper
 * stock. So the options below vary those axes instead of varying an alpha.
 */
type Fill = 'brand' | 'neutral' | 'warm' | 'ink' | 'none'
type Marker = 'bar' | 'hairline' | 'dot' | 'none'

interface StateLayer {
  fill: Fill
  /** Mix toward the fill colour. 1 = the fill colour itself, used by `warm` and `ink`. */
  amount: number
}

interface Treatment {
  hover: StateLayer
  selected: StateLayer
  marker: Marker
  /** Row corner radius in px. 0 is the editorial choice; 8 is today's. */
  radius: number
  /** Hairline rules between rows — the auction-catalogue lot list. */
  rules?: boolean
  /** Letterspaced small-caps labels rather than sentence case. */
  smallCaps?: boolean
}

interface StateVariant {
  key: string
  label: string
  summary: string
  /** Where it sits on the dev-tool ↔ editorial axis, stated so the set is legible as a range. */
  register: string
  light: Treatment
  dark: Treatment
}

/**
 * Warm and ink anchors per mode.
 *
 * `warm` is the paper stock: an ivory in light, and in dark a WARM charcoal rather than the
 * cool zinc the app uses everywhere else — the equivalent move, since a warm dark reads as ink
 * on board while a cool dark reads as a screen.
 *
 * `ink` is a full inversion, which is only viable because exactly one row is ever selected.
 */
const WARM: Record<Mode, RGB> = { light: hexToRgb('#f6f1e8'), dark: hexToRgb('#221e18') }
const INK: Record<Mode, { fill: string; text: string }> = {
  light: { fill: '#18181b', text: '#faf8f4' },
  dark: { fill: '#faf8f4', text: '#18181b' },
}

const fillBase = (fill: Fill, mode: Mode): RGB => {
  if (fill === 'brand') return BRAND_RGB
  if (fill === 'warm') return WARM[mode]
  if (fill === 'ink') return hexToRgb(INK[mode].fill)
  return MODE_TOKENS[mode].neutral
}

/** Same treatment in both modes, with only the amounts differing. Keeps the table readable. */
const bothModes = (
  t: Omit<Treatment, 'hover' | 'selected'>,
  light: { hover: StateLayer; selected: StateLayer },
  dark: { hover: StateLayer; selected: StateLayer },
): Pick<StateVariant, 'light' | 'dark'> => ({
  light: { ...t, ...light },
  dark: { ...t, ...dark },
})

const STATE_VARIANTS: StateVariant[] = [
  {
    key: 'today-menu',
    label: '1 — Today (menus + nav pills)',
    register: 'consumer SaaS',
    summary:
      'Brand tint at 12%/16%, rounded 8px, no marker, one mode-blind number for both themes. The baseline everything else is measured against.',
    ...bothModes(
      { marker: 'none', radius: 8 },
      { hover: { fill: 'brand', amount: 0.12 }, selected: { fill: 'brand', amount: 0.16 } },
      { hover: { fill: 'brand', amount: 0.12 }, selected: { fill: 'brand', amount: 0.16 } },
    ),
  },
  {
    key: 'proposal',
    label: '2 — Mode-aware + 2px marker',
    register: 'developer tool',
    summary:
      'My earlier proposal, kept for comparison rather than quietly dropped. Fixes the measurement problem and is the most conventional answer on screen — which is the objection to it.',
    ...bothModes(
      { marker: 'bar', radius: 8 },
      { hover: { fill: 'brand', amount: 0.05 }, selected: { fill: 'brand', amount: 0.09 } },
      { hover: { fill: 'brand', amount: 0.1 }, selected: { fill: 'brand', amount: 0.18 } },
    ),
  },
  {
    key: 'type-led',
    label: '3 — Type-led (no fill at all)',
    register: 'editorial / Artsy',
    summary:
      'Nothing fills. Hover lifts the label from secondary to primary; selected adds weight 600. Depth entirely from type. The purest version of the register — and the least discoverable, which is the honest cost in a nav used forty times a day.',
    ...bothModes(
      { marker: 'none', radius: 0 },
      { hover: { fill: 'none', amount: 0 }, selected: { fill: 'none', amount: 0 } },
      { hover: { fill: 'none', amount: 0 }, selected: { fill: 'none', amount: 0 } },
    ),
  },
  {
    key: 'ruled-index',
    label: '4 — Ruled index + 1px hairline',
    register: 'auction catalogue',
    summary:
      'Rows sit on hairline rules like a lot list, square corners, and the selected row is marked by a 1px INK hairline plus weight — not a 2px brand bar. Reads as an index rather than a control panel. Brand appears nowhere in the nav.',
    ...bothModes(
      { marker: 'hairline', radius: 0, rules: true },
      { hover: { fill: 'warm', amount: 0.5 }, selected: { fill: 'warm', amount: 1 } },
      { hover: { fill: 'warm', amount: 0.5 }, selected: { fill: 'warm', amount: 1 } },
    ),
  },
  {
    key: 'paper-wash',
    label: '5 — Paper wash (warm, square, no marker)',
    register: 'gallery print room',
    summary:
      'Selected takes the ivory paper stock at full strength, square, no marker, weight 600; hover is the same stock at half. Depth by TEMPERATURE rather than luminance — which is why it works where a lighter fill cannot: white is a ceiling, warmth is not.',
    ...bothModes(
      { marker: 'none', radius: 2 },
      { hover: { fill: 'warm', amount: 0.45 }, selected: { fill: 'warm', amount: 1 } },
      { hover: { fill: 'warm', amount: 0.5 }, selected: { fill: 'warm', amount: 1 } },
    ),
  },
  {
    key: 'ink-block',
    label: '6 — Ink block (inverse selected)',
    register: 'Sotheby’s signage',
    summary:
      'The selected row inverts to ink with paper-coloured text, square. Only ever one row, so a single confident block is legible rather than loud. Most brand-forward without using the brand hue at all — and the boldest departure, so the one most likely to feel wrong at small sizes.',
    ...bothModes(
      { marker: 'none', radius: 2, smallCaps: true },
      { hover: { fill: 'warm', amount: 0.5 }, selected: { fill: 'ink', amount: 1 } },
      { hover: { fill: 'warm', amount: 0.55 }, selected: { fill: 'ink', amount: 1 } },
    ),
  },
]

const NAV_ROWS = [
  { label: 'Profile', icon: UserIcon, state: 'rest' as const },
  { label: 'Notifications', icon: BellIcon, state: 'hover' as const },
  { label: 'Users', icon: UsersIcon, state: 'selected' as const },
  { label: 'Billing', icon: ReceiptIcon, state: 'rest' as const },
]

const SIDEBAR_SURFACE: Record<Mode, string> = { light: '#fafafa', dark: '#18181b' }

/** One variant in one mode: the three row states on that mode's sidebar surface. */
const StateBlock = ({ variant, mode }: { variant: StateVariant; mode: Mode }) => {
  const t = variant[mode]
  const tokens = MODE_TOKENS[mode]
  const surface = hexToRgb(SIDEBAR_SURFACE[mode])

  const resolve = (layer: StateLayer) => {
    if (layer.fill === 'none') return null
    const eff = composite(fillBase(layer.fill, mode), surface, layer.amount)
    return {
      hex: toHex(eff),
      deltaL: luminance(eff) - luminance(surface),
      contrast: contrast(eff, surface),
      chroma: chroma(eff),
      // Perceptual distance, which is the number that matters for a warm/cool shift: two
      // surfaces can be a luminance no-op and still read as different materials.
      deltaE: deltaE(eff, surface),
    }
  }

  const hover = resolve(t.hover)
  const selected = resolve(t.selected)
  const inkSelected = t.selected.fill === 'ink'

  return (
    <Stack spacing={2}>
      <ModeTag mode={mode} />
      <Box sx={{ backgroundColor: SIDEBAR_SURFACE[mode], borderRadius: 3, p: 2 }}>
        {NAV_ROWS.map(({ label, icon: Icon, state }, i) => {
          const active = state === 'selected'
          const layer = state === 'hover' ? hover : active ? selected : null
          const text = active
            ? inkSelected
              ? INK[mode].text
              : tokens.text
            : state === 'hover'
              ? tokens.text
              : tokens.dim
          return (
            <Stack
              key={label}
              direction="row"
              alignItems="center"
              spacing={3}
              sx={{
                height: 38,
                px: 3,
                borderRadius: `${t.radius}px`,
                backgroundColor: layer ? layer.hex : 'transparent',
                // A 1px ink hairline versus a 2px brand bar is most of the register difference:
                // one is a rule, the other is a UI accent.
                borderLeft:
                  t.marker === 'bar' && active
                    ? `2px solid ${BRAND}`
                    : t.marker === 'hairline' && active
                      ? `1px solid ${tokens.text}`
                      : t.marker === 'bar' || t.marker === 'hairline'
                        ? '2px solid transparent'
                        : undefined,
                borderBottom:
                  t.rules && i < NAV_ROWS.length - 1
                    ? `1px solid ${toHex(composite(tokens.neutral, surface, 0.1))}`
                    : undefined,
                color: text,
                fontWeight: active ? 600 : 400,
              }}
            >
              {t.marker === 'dot' && (
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    backgroundColor: active ? tokens.text : 'transparent',
                  }}
                />
              )}
              <Icon size={15} strokeWidth={1} />
              <Typography
                variant="body4"
                sx={{
                  fontWeight: 'inherit',
                  color: 'inherit',
                  ...(t.smallCaps
                    ? { textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: 11 }
                    : {}),
                }}
              >
                {label}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="body4" sx={{ color: 'inherit', opacity: 0.5 }}>
                {state}
              </Typography>
            </Stack>
          )
        })}
      </Box>

      {/* ΔE is printed alongside ΔL because the warm options are deliberately near-invisible
          on the luminance axis. Judging them by contrast alone would rank them last for the
          exact property that makes them work. */}
      <Stack spacing={0.25}>
        {(
          [
            ['hover', t.hover, hover],
            ['selected', t.selected, selected],
          ] as const
        ).map(([name, layer, m]) => (
          <Typography key={name} variant="body4" color="text.secondary">
            {name}{' '}
            {m === null
              ? '— no fill (type only)'
              : `${layer.fill} @${layer.amount} → ${m.hex} · ΔL ${m.deltaL > 0 ? '+' : ''}${m.deltaL.toFixed(
                  3,
                )} · ${m.contrast.toFixed(3)}:1 · ΔE ${m.deltaE} · chroma ${m.chroma}`}
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}

const StateColumn = ({ variant }: { variant: StateVariant }) => (
  <Stack spacing={3} sx={{ flex: 1, minWidth: 300 }}>
    <Stack spacing={1}>
      <Typography variant="body3" sx={{ fontWeight: 600 }}>
        {variant.label}
      </Typography>
      <Typography variant="body4" sx={{ opacity: 0.5, letterSpacing: '0.06em' }}>
        {variant.register.toUpperCase()}
      </Typography>
      <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {variant.summary}
      </Typography>
    </Stack>
    <StateBlock variant={variant} mode="light" />
    <StateBlock variant={variant} mode="dark" />
    <Typography variant="body4" sx={{ opacity: 0.55 }}>
      radius {variant.light.radius}px · marker {variant.light.marker}
      {variant.light.rules ? ' · hairline rules' : ''}
    </Typography>
  </Stack>
)

// ---------------------------------------------------------------------------
// Panel B
// ---------------------------------------------------------------------------

interface SurfaceConfig {
  page: string
  sidebar: string
  popover: string
  borderAlpha: number
  shadow: number
  /** Popover corner radius in px. 12 is today's; 0-2 is the printed-card register. */
  radius?: number
}

interface SurfaceVariant {
  key: string
  label: string
  summary: string
  light: SurfaceConfig
  dark: SurfaceConfig
}

/**
 * Dark is deliberately IDENTICAL across the first three variants — its ramp needs no change,
 * and pretending otherwise to make the lab look busier would be dishonest. It is rendered
 * anyway so the light column can be compared against the thing it is trying to match, and so
 * "dark is already fine" is visible rather than asserted. Only the last variant touches dark,
 * and only its border.
 */
const SURFACE_VARIANTS: SurfaceVariant[] = [
  {
    key: 'today',
    label: 'Today',
    summary:
      'Popover pinned at white with a 12% border and a heavy shadow doing the separating. The shadow is compensation for an edge that does not read.',
    light: { page: '#f4f5f5', sidebar: '#fafafa', popover: '#ffffff', borderAlpha: 0.12, shadow: 6 },
    dark: { page: '#09090b', sidebar: '#18181b', popover: '#0f0f12', borderAlpha: 0.12, shadow: 3 },
  },
  {
    key: 'border',
    label: 'Stronger edge, minimal shadow',
    summary:
      'Same surfaces; border to 20% (1.470:1 against the page instead of 1.204:1) and the shadow dropped to the dark-mode weight. The cheap fix: a line needs only ~1.3:1, which is reachable where a fill step is not. Contained to the popover paper.',
    light: { page: '#f4f5f5', sidebar: '#fafafa', popover: '#ffffff', borderAlpha: 0.2, shadow: 2 },
    dark: { page: '#09090b', sidebar: '#18181b', popover: '#0f0f12', borderAlpha: 0.12, shadow: 2 },
  },
  {
    key: 'ramp',
    label: 'Three surfaces (page lowered)',
    summary:
      'Page drops to #e9ebeb so the popover is no longer at a ceiling: steps of 1.096 and 1.092 against dark’s 1.04 and 1.08. The only option that actually gives light the three-colour ramp dark has. Cost: it is global — every page, table and card gets a greyer backdrop.',
    light: { page: '#e9ebeb', sidebar: '#f4f5f5', popover: '#ffffff', borderAlpha: 0.16, shadow: 1 },
    dark: { page: '#09090b', sidebar: '#18181b', popover: '#0f0f12', borderAlpha: 0.12, shadow: 1 },
  },
  {
    key: 'flat-warm',
    label: 'Flat — warm paper, hairline, NO shadow',
    summary:
      'Zero elevation. The popover is ivory on a cool page, so it separates by TEMPERATURE rather than luminance: 1.075:1 but ΔE 3.68, i.e. perceptually further apart than today’s white-on-grey despite a lower contrast ratio. Square-ish corners (2px) and a 1px ink hairline. Depth by material, the way paper sits on board.',
    light: {
      page: '#f2f3f4',
      sidebar: '#fafafa',
      popover: '#fdfbf6',
      borderAlpha: 0.2,
      shadow: 0,
      radius: 2,
    },
    dark: {
      page: '#09090b',
      sidebar: '#18181b',
      popover: '#1a1714',
      borderAlpha: 0.16,
      shadow: 0,
      radius: 2,
    },
  },
  {
    key: 'flat-greige',
    label: 'Flat — greige ground, white card, square',
    summary:
      'The inverse temperature story: a warm greige GROUND with a cool white card on it, square corners, hairline, no shadow. Highest perceptual separation measured here (ΔE 4.61 at 1.119:1) and the most gallery-like — a white print on a warm mat.',
    light: {
      page: '#f3f2f0',
      sidebar: '#f8f7f5',
      popover: '#ffffff',
      borderAlpha: 0.2,
      shadow: 0,
      radius: 0,
    },
    dark: {
      page: '#0b0a09',
      sidebar: '#1a1815',
      popover: '#121110',
      borderAlpha: 0.16,
      shadow: 0,
      radius: 0,
    },
  },
  {
    key: 'ramp-border',
    label: 'Three surfaces + stronger edge',
    summary:
      'Both fixes, no meaningful shadow. If this reads best, the shadow was never carrying definition — it was covering for a weak edge and a ramp with no room.',
    light: { page: '#e9ebeb', sidebar: '#f4f5f5', popover: '#ffffff', borderAlpha: 0.2, shadow: 0 },
    dark: { page: '#09090b', sidebar: '#18181b', popover: '#0f0f12', borderAlpha: 0.16, shadow: 0 },
  },
]

/** A miniature page + sidebar + overlapping popover, at one variant's values in one mode. */
const SurfaceBlock = ({ variant, mode }: { variant: SurfaceVariant; mode: Mode }) => {
  const theme = useTheme()
  const cfg = variant[mode]
  const tokens = MODE_TOKENS[mode]

  const page = hexToRgb(cfg.page)
  const sidebar = hexToRgb(cfg.sidebar)
  const popover = hexToRgb(cfg.popover)
  const edge = composite(tokens.neutral, popover, cfg.borderAlpha)

  const steps = [
    ['page→sidebar', contrast(page, sidebar)],
    ['page→popover', contrast(page, popover)],
    ['edge vs page', contrast(edge, page)],
  ] as const

  // The warm options are deliberately near-flat on the luminance axis, so ranking them by
  // contrast alone would mark them down for the exact property that makes them work.
  const popoverDeltaE = deltaE(popover, page)

  return (
    <Stack spacing={2}>
      <ModeTag mode={mode} />
      <Box
        sx={{
          position: 'relative',
          height: 180,
          backgroundColor: cfg.page,
          borderRadius: 3,
          overflow: 'hidden',
          // A hairline so the mock's own bounds are not mistaken for the page colour.
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            right: 'auto',
            width: 104,
            backgroundColor: cfg.sidebar,
            p: 2,
          }}
        >
          {['Help', 'Settings'].map((l, i) => (
            <Stack
              key={l}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                height: 30,
                px: 2,
                borderRadius: 2,
                // The second row is shown selected, so the popover is judged next to an active
                // row — which is how it actually appears.
                backgroundColor: i === 1 ? toHex(composite(tokens.neutral, sidebar, 0.06)) : 'transparent',
                color: i === 1 ? tokens.text : tokens.dim,
              }}
            >
              <SettingsIcon size={13} strokeWidth={1} />
              <Typography variant="body4" sx={{ color: 'inherit' }}>
                {l}
              </Typography>
            </Stack>
          ))}
        </Box>

        {/* Overlapping the sidebar/page boundary, so the edge is seen against BOTH. */}
        <Box
          sx={{
            position: 'absolute',
            left: 84,
            top: 42,
            width: 150,
            backgroundColor: cfg.popover,
            border: `1px solid ${toHex(edge)}`,
            borderRadius: `${cfg.radius ?? 12}px`,
            boxShadow: cfg.shadow === 0 ? 'none' : theme.shadows[cfg.shadow],
            p: 2,
          }}
        >
          {['Profile', 'Theme', 'Log out'].map(l => (
            <Stack
              key={l}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ height: 27, px: 2, borderRadius: 2, color: tokens.dim }}
            >
              <UserIcon size={13} strokeWidth={1} />
              <Typography variant="body4" sx={{ color: 'inherit' }}>
                {l}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>

      <Stack spacing={0.25}>
        {steps.map(([name, v]) => (
          <Typography key={name} variant="body4" color="text.secondary">
            {name} {v.toFixed(3)}:1
            {name === 'edge vs page' && v < 1.3 ? '  ← under the ~1.3 line threshold' : ''}
          </Typography>
        ))}
        <Typography variant="body4" color="text.secondary">
          page→popover ΔE {popoverDeltaE}
          {popoverDeltaE > 4 ? '  ← perceptually further than today (3.55)' : ''}
        </Typography>
        <Typography variant="body4" sx={{ opacity: 0.55 }}>
          border {cfg.borderAlpha} · shadow {cfg.shadow === 0 ? 'none' : cfg.shadow} · radius{' '}
          {cfg.radius ?? 12}px
        </Typography>
      </Stack>
    </Stack>
  )
}

const SurfaceMock = ({ variant }: { variant: SurfaceVariant }) => (
  <Stack spacing={3} sx={{ flex: 1, minWidth: 300 }}>
    <Stack spacing={1}>
      <Typography variant="body3" sx={{ fontWeight: 600 }}>
        {variant.label}
      </Typography>
      <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {variant.summary}
      </Typography>
    </Stack>
    <SurfaceBlock variant={variant} mode="light" />
    <SurfaceBlock variant={variant} mode="dark" />
  </Stack>
)

// ---------------------------------------------------------------------------

export const NavChromeLab = () => {
  const theme = useTheme()
  const [panel, setPanel] = useState<'states' | 'surfaces'>('states')

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Nav + menu chrome
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Each option is painted in BOTH modes from explicit hex values, so nothing here depends
          on the theme you happen to be in — the argument is the difference between the modes,
          and that cannot be judged one mode at a time.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={3}>
        <Button
          variant={panel === 'states' ? 'primary' : 'secondary'}
          onClick={() => setPanel('states')}
        >
          A — row states
        </Button>
        <Button
          variant={panel === 'surfaces' ? 'primary' : 'secondary'}
          onClick={() => setPanel('surfaces')}
        >
          B — popover surface
        </Button>
      </Stack>

      {panel === 'states' ? (
        <>
          <Card sx={{ m: 0 }}>
            <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Watch ΔL, not the swatch. In light every fill goes NEGATIVE — white is a ceiling,
              so a tint can only darken — while in dark the same tint goes positive. That sign
              flip is why light cannot be fixed by copying dark&apos;s alpha, and it is the whole
              reason today&apos;s single mode-blind number cannot be right in both places.
            </Typography>
          </Card>
          <Stack direction="row" spacing={6} sx={{ flexWrap: 'wrap', rowGap: 6 }}>
            {STATE_VARIANTS.map(v => (
              <StateColumn key={v.key} variant={v} />
            ))}
          </Stack>
        </>
      ) : (
        <>
          <Card sx={{ m: 0 }}>
            <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Dark is intentionally the same in the first three options: its ramp needs no change,
              and the honest way to show that is to render it unchanged rather than invent
              variation. It is here so light can be compared against what it is trying to match.
              Dark&apos;s fill steps are NOT bigger than light&apos;s (1.04/1.08 vs 1.046/1.044) —
              it reads better because its popover sits between page and sidebar instead of at a
              ceiling, and because its edge reads.
            </Typography>
          </Card>
          <Stack direction="row" spacing={6} sx={{ flexWrap: 'wrap', rowGap: 6 }}>
            {SURFACE_VARIANTS.map(v => (
              <SurfaceMock key={v.key} variant={v} />
            ))}
          </Stack>
        </>
      )}

      <Card sx={{ m: 0, borderColor: alpha(theme.palette.text.primary, 0.08) }}>
        <Stack spacing={2}>
          <Typography variant="body3" sx={{ fontWeight: 600 }}>
            What shipping the winner touches
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Panel A wants a single source — a `navChrome.ts` beside `fieldChrome.ts` — read by
            the Sidebar, `SideNav`, and the `MuiMenuItem` override. Three call sites each state
            their own answer today, which is exactly how the field borders drifted: a component
            that re-derives &quot;the same as X&quot; will silently stop matching X.
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Panel B&apos;s ramp option changes `background.default` in light, so it reaches every
            page, table and card at once — the one variant here that is not a local change. It
            should be re-checked against the data table and the field fills before it lands. The
            border-only option is contained to the popover paper.
          </Typography>
        </Stack>
      </Card>
    </Stack>
  )
}
